<template>
	<!-- A small line chart: one line per series over shared steps. Defaults suit faction
	     standing (-4 hostile to +4 allied); min/max/bands make it work for counts too -->
	<div class="standing-chart">
		<svg v-if="steps.length > 1" ref="svg" :viewBox="`0 0 ${W} ${H}`" class="chart-svg">
			<!-- bands and gridlines -->
			<template v-if="bands">
				<rect
					:x="PAD_L"
					:y="y(top)"
					:width="W - PAD_L - PAD_R"
					:height="y(0) - y(top)"
					class="band allied"
				/>
				<rect
					:x="PAD_L"
					:y="y(0)"
					:width="W - PAD_L - PAD_R"
					:height="y(bottom) - y(0)"
					class="band hostile"
				/>
			</template>
			<line
				v-for="v in gridLines"
				:key="v"
				:x1="PAD_L"
				:x2="W - PAD_R"
				:y1="y(v)"
				:y2="y(v)"
				class="grid"
			/>
			<line v-if="bottom < 0" :x1="PAD_L" :x2="W - PAD_R" :y1="y(0)" :y2="y(0)" class="zero" />
			<text
				v-for="v in tickValues"
				:key="'t' + v"
				:x="PAD_L - 8"
				:y="y(v) + 4"
				class="tick"
				text-anchor="end"
			>
				{{ signed && v > 0 ? "+" + v : v }}
			</text>
			<text
				v-for="(label, i) in steps"
				:key="'x' + i"
				:x="x(i)"
				:y="H - 6"
				class="tick"
				text-anchor="middle"
			>
				{{ label }}
			</text>
			<!-- lines -->
			<g v-for="s in series" :key="s.name">
				<path :d="path(s)" class="line" :stroke="s.color" pathLength="1" />
				<circle
					v-for="(p, i) in points(s)"
					:key="i"
					:cx="p.x"
					:cy="p.y"
					r="3.5"
					:fill="s.color"
					class="dot"
				/>
			</g>
		</svg>
		<div v-else class="chart-empty">
			<span>// NO CHANGES RECORDED //</span>
			<small v-if="emptyHint" v-html="emptyHint"></small>
		</div>
		<div v-if="series.length && steps.length > 1" class="chart-legend">
			<span v-for="s in series" :key="s.name" class="legend-item">
				<span class="legend-swatch" :style="{ background: s.color }"></span>{{ s.name }}
			</span>
		</div>
	</div>
</template>

<script>
import { animate, stagger } from "motion";

export default {
	props: {
		// [{ name, color, history: [{ when, status }] }]
		series: { type: Array, required: true },
		animate: { type: Boolean, default: true },
		min: { type: Number, default: -4 },
		// null = fit to the data
		max: { type: Number, default: 4 },
		bands: { type: Boolean, default: true },
		signed: { type: Boolean, default: true },
		emptyHint: {
			type: String,
			default:
				"Add <code>history:</code> entries to a faction file to chart its standing over time.",
		},
	},
	data() {
		return { W: 600, H: 220, PAD_L: 34, PAD_R: 14, PAD_T: 12, PAD_B: 26 };
	},
	computed: {
		// Every distinct "when", in the order they first appear
		bottom() {
			return this.min;
		},
		top() {
			if (this.max !== null) return this.max;
			const peak = Math.max(1, ...this.series.flatMap(s => s.history.map(h => h.status)));
			return Math.ceil(peak / 4) * 4;
		},
		tickValues() {
			return this.bottom < 0
				? [this.top, 0, this.bottom]
				: [this.top, Math.round(this.top / 2), this.bottom];
		},
		gridLines() {
			const span = this.top - this.bottom;
			return [0.25, 0.5, 0.75]
				.map(f => this.bottom + span * f)
				.concat(this.bottom < 0 ? [] : [this.bottom]);
		},
		steps() {
			const seen = [];
			this.series.forEach(s => s.history.forEach(h => !seen.includes(h.when) && seen.push(h.when)));
			return seen;
		},
	},
	watch: {
		series() {
			this.$nextTick(this.draw);
		},
	},
	mounted() {
		if (this.animate) this.draw();
	},
	methods: {
		x(i) {
			const span = this.W - this.PAD_L - this.PAD_R;
			return this.PAD_L + (this.steps.length > 1 ? (i / (this.steps.length - 1)) * span : span / 2);
		},
		y(v) {
			const span = this.H - this.PAD_T - this.PAD_B;
			return this.PAD_T + ((this.top - v) / (this.top - this.bottom || 1)) * span;
		},
		points(s) {
			return s.history.map(h => ({
				x: this.x(this.steps.indexOf(h.when)),
				y: this.y(Math.max(this.bottom, Math.min(this.top, h.status))),
			}));
		},
		path(s) {
			return this.points(s)
				.map((p, i) => `${i ? "L" : "M"}${p.x},${p.y}`)
				.join(" ");
		},
		// Lines draw left to right, then their points pop in
		draw() {
			const svg = this.$refs.svg;
			if (!svg || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
			animate(
				svg.querySelectorAll(".line"),
				{ strokeDashoffset: [1, 0] },
				{ duration: 1.2, delay: stagger(0.12, { startDelay: 0.3 }), ease: [0.22, 1, 0.36, 1] }
			);
			animate(
				svg.querySelectorAll(".dot"),
				{ opacity: [0, 1] },
				{ duration: 0.3, delay: stagger(0.04, { startDelay: 0.9 }) }
			);
		},
	},
};
</script>

<style scoped>
.standing-chart {
	display: flex;
	flex-direction: column;
	gap: 0.6em;
	flex: 1;
	min-height: 0;
}

.chart-svg {
	width: 100%;
	flex: 1;
	min-height: 160px;
	overflow: visible;
}

.band.allied {
	fill: rgba(59, 122, 87, 0.06);
}
.band.hostile {
	fill: rgba(192, 57, 79, 0.06);
}

.grid {
	stroke: rgba(209, 200, 184, 0.08);
	stroke-width: 1;
	vector-effect: non-scaling-stroke;
}

.zero {
	stroke: rgba(209, 200, 184, 0.3);
	stroke-dasharray: 4 4;
	vector-effect: non-scaling-stroke;
}

.tick {
	font-family: "Share Tech Mono", monospace;
	font-size: 10px;
	fill: rgba(209, 200, 184, 0.45);
}

.line {
	fill: none;
	stroke-width: 2;
	stroke-dasharray: 1;
	stroke-linejoin: round;
	vector-effect: non-scaling-stroke;
}

.chart-empty {
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.5em;
	text-align: center;
	font-family: "Share Tech Mono", monospace;
	font-size: 11px;
	letter-spacing: 0.1em;
	color: rgba(209, 200, 184, 0.45);
}

.chart-empty small {
	font-size: 10px;
	letter-spacing: 0.05em;
	color: rgba(209, 200, 184, 0.35);
}

.chart-legend {
	display: flex;
	flex-wrap: wrap;
	gap: 0.3em 1em;
}

.legend-item {
	display: inline-flex;
	align-items: center;
	gap: 0.4em;
	font-family: "Share Tech Mono", monospace;
	font-size: 10px;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--text-pilot-code);
}

.legend-swatch {
	width: 12px;
	height: 3px;
}
</style>
